// The evidence / Trace surface (Brief 4 / P2-A) — UC-3 "Wait, why?". Built
// P0a-PURE (every color a `var(--waytide-*)` token, so `[data-appearance]` drives
// it). Renders a session's `trace_event` rows chronologically; ONE TAP on a row
// resolves its `evidence_ref` to the CHECKABLE artifact (the real change), not a
// narrated rationale. Reuses the shared, token-agnostic `tracePresentation`
// helpers; the resolve uses the GET /trace/evidence endpoint (chat: inline;
// sha:/run: external). Confidence cue is a follow-on slice (its own sidecar +
// tokens). The free-type steer box (P2-B) is separate and gated behind the
// redactor (P2-C, shipped).

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getEvidence, getTrace } from "../api/client";
import type { EvidenceResponse, TraceEvent } from "@waytide/core";
import { sessionName, sessionLabel } from "@waytide/core";
import { usePresenceContext } from "@waytide/ui";
import {
  formatTraceCost,
  formatTraceWhen,
  tracePresentation,
} from "@waytide/core/tracePresentation";
import { toneColorVar } from "./traceTone";
import { BrakeControl } from "../steer/BrakeControl";
import { AuthorityDial } from "../govern/AuthorityDial";
import { RadioControl } from "../radio/RadioControl";
import "./trace.css";

export function EvidencePage() {
  const { project } = useParams<{ project: string }>();
  const { sessions } = usePresenceContext();
  // Name this session by its concrete identity when it's live in presence;
  // fall back to the stable handle (the trace can outlive the live session).
  const session = sessions.find((s) => s.thread_id === Number(project));
  const label = session ? sessionLabel(session) : sessionName(Number(project));
  const [events, setEvents] = useState<TraceEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!project) return;
    let alive = true;
    setEvents(null);
    setError(null);
    getTrace(project)
      .then((r) => alive && setEvents(r.events))
      .catch(
        (e) => alive && setError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      alive = false;
    };
  }, [project]);

  return (
    <div className="wt-trace">
      <header className="wt-trace__head">
        {/* The way back to the Dashboard — points straight at /dashboard (not
            "/") so the return stays in-product on the Tauri shell too. */}
        <Link className="wt-trace__back" to="/dashboard" viewTransition>
          ‹ Dashboard
        </Link>
        <div className="wt-trace__headrow">
          <div className="wt-trace__heading">
            <h1 className="wt-trace__title">Evidence</h1>
            {/* The session identity the session cell morphs INTO (shared
                view-transition-name) — the cell becomes this header. */}
            {project ? (
              <span
                className="wt-trace__session"
                style={{ viewTransitionName: `session-${project}` }}
                title={`Session #${project}`}
              >
                {label}
              </span>
            ) : null}
          </div>
          {/* Judge + steer from one seat (UC-3): stop/resume this session here. */}
          {project ? <BrakeControl threadId={Number(project)} /> : null}
        </div>
        <p className="wt-trace__sub">
          The work behind this session. One tap reaches the checkable artifact —
          the real change, not a rationale.
        </p>
      </header>

      {/* Govern (P3): set this session's autonomy from the same seat. */}
      {project ? (
        <AuthorityDial scope={{ kind: "session", threadId: Number(project) }} />
      ) : null}

      {/* Radio (P4a): voice this session's recent trace as a metered clip —
          opt-in, the spend shown, stoppable. The eyes-free face, audible from
          the same seat; debits the session budget on the server. */}
      {project ? (
        <RadioControl
          threadId={Number(project)}
          events={events ?? []}
          label={label}
        />
      ) : null}

      {!project ? (
        <p className="wt-trace__empty">No session selected.</p>
      ) : error ? (
        <p className="wt-trace__empty">Couldn't load the trace: {error}</p>
      ) : !events ? (
        <p className="wt-trace__empty">Reading the trace…</p>
      ) : events.length === 0 ? (
        <p className="wt-trace__empty">No evidence yet for this session.</p>
      ) : (
        <ol className="wt-trace__list">
          {events.map((e) => (
            <TraceRow
              key={`${e.id}-${e.created_at}`}
              project={project}
              event={e}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function TraceRow({ project, event }: { project: string; event: TraceEvent }) {
  const p = tracePresentation(event.kind);
  const when = formatTraceWhen(event.created_at);
  const cost = formatTraceCost(event.cost_micros);
  return (
    <li className="wt-trace__row">
      <div className="wt-trace__rowhead">
        <span className="wt-trace__chip" style={{ color: toneColorVar(p.tone) }}>
          <span aria-hidden="true">{p.icon}</span> {p.label}
        </span>
        <span className="wt-trace__summary">{event.summary || "—"}</span>
        <span className="wt-trace__meta">
          {when ? <span className="wt-trace__when">{when}</span> : null}
          {/* cost is quiet, in text.cost, never animated, never the attention channel */}
          {cost ? <span className="wt-trace__cost">{cost}</span> : null}
        </span>
      </div>
      {event.evidence_ref ? (
        <EvidenceView project={project} evidenceRef={event.evidence_ref} />
      ) : null}
    </li>
  );
}

type ViewState = "idle" | "loading" | "shown" | "error";

function EvidenceView({
  project,
  evidenceRef,
}: {
  project: string;
  evidenceRef: string;
}) {
  const [state, setState] = useState<ViewState>("idle");
  const [skeleton, setSkeleton] = useState(false);
  const [artifact, setArtifact] = useState<EvidenceResponse | null>(null);
  // Immutable artifacts (chat/sha/run are content-addressed) — cache so a second
  // look is instant and free.
  const cache = useRef<Map<string, EvidenceResponse>>(new Map());

  function toggle() {
    if (state === "shown" || state === "loading") {
      setState("idle");
      return;
    }
    const cached = cache.current.get(evidenceRef);
    if (cached) {
      setArtifact(cached);
      setState("shown");
      return;
    }
    setState("loading");
    setSkeleton(false);
    // Delay-gate: show NOTHING for the first 500ms — the resolve usually
    // lands inside that and feels instant; only mount the skeleton past 500ms, so
    // there is never a spinner that appears at 100ms and loops till 400ms.
    const timer = setTimeout(() => setSkeleton(true), 500);
    getEvidence(project, evidenceRef)
      .then((r) => {
        clearTimeout(timer);
        cache.current.set(evidenceRef, r);
        setArtifact(r);
        setState("shown");
      })
      .catch(() => {
        clearTimeout(timer);
        setState("error");
      });
  }

  return (
    <div className="wt-trace__evidence">
      <button type="button" className="wt-trace__view" onClick={toggle}>
        {state === "shown" ? "Hide evidence" : "View evidence"}
        <span className="wt-trace__ref"> · {evidenceRef}</span>
      </button>
      {state === "loading" && skeleton ? (
        <div className="wt-trace__skeleton" aria-hidden="true" />
      ) : null}
      {state === "error" ? (
        <p className="wt-trace__artifact wt-trace__artifact--quiet">
          Couldn't reach that artifact.
        </p>
      ) : null}
      {state === "shown" && artifact ? (
        artifact.resolved ? (
          <pre className="wt-trace__artifact">{artifact.text}</pre>
        ) : (
          <p className="wt-trace__artifact wt-trace__artifact--quiet">
            External {artifact.kind} reference — {artifact.ref}
          </p>
        )
      ) : null}
    </div>
  );
}
